import { useSnackbarStore } from '@components/shared/Snackbar/store';
import { useTranslation } from 'react-i18next';
import { completeUpload, getPartUrl, initiateUpload } from './file-managerService';
import { useUploadStore } from './file-managerStore';
/**
 * @module useMultipartUpload
 */

/**
 * Custom hook for handling multipart file uploads with progress tracking.
 * Supports uploading multiple files by splitting them into chunks and uploading them in parallel.

 * @example
 * import { useFetchFarmerDashboard } from '@features/dashboard/store';
 * import { LinearProgress, Typography } from '@mui/material';
 * import Button from '@mui/material/Button';
 * import React from 'react';
 * import { useMultipartUpload } from 'services/file-manager';
 *
 * export const FarmerDashboardPage = () => {
 * 	const [selectedFiles, setSelectedFiles] = React.useState<File[]>([]);
 * 	const { uploadStatus, uploadFiles, isAllCompleted, globalLoading, failedUploads, inProgressUploads, togglePause } = useMultipartUpload({
 * 		allowedFormats: ['.pdf', '.doc', '.docx'],
 * 		maxFileSize: 50 * 1024 * 1024,
 * 		onProgress: (progress, fileName) => {
 * 			console.log(`Progress for ${fileName}: ${progress}%`);
 * 		},
 * 		onError: (error, fileName) => {
 * 			console.error(`Error uploading ${fileName}:`, error);
 * 		},
 * 		onComplete: (results) => {
 * 			console.log('Upload complete:', results);
 * 		}
 * 	});
 *
 * 	const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
 * 		const files = Array.from(event.target.files || []);
 * 		setSelectedFiles(files);
 * 		if (files.length > 0) {
 * 			await uploadFiles(files);
 * 			event.target.value = '';
 * 		}
 * 	};
 *
 * 	return (
 * 		<div>
 * 			<Typography variant="h6">Multipart File Upload</Typography>
 * 			<input type="file" multiple onChange={handleFileChange} />
 * 			{globalLoading && <Typography>Uploading files...</Typography>}
 * 			{uploadStatus.map((status, index) => (
 * 				<div key={status.key || index}>
 * 					<Typography>{status.fileName}</Typography>
 * 					<LinearProgress variant="determinate" value={status.progress} />
 * 					<Typography>{status.progress}%</Typography>
 * 					{status.error && <Typography color="error">Error: {status.error}</Typography>}
 * 					{status.isCompleted && <Typography color="success">Complete!</Typography>}
 * 					{!status.isCompleted && !status.error && (
 * 						<Button onClick={() => togglePause(index)}>
 * 							{status.isPaused ? 'Resume' : 'Pause'}
 * 						</Button>
 * 					)}
 * 				</div>
 * 			))}
 * 		</div>
 * 	);
 * };
 */

/**
 * @constant {number} CHUNK_SIZE - Size of each file chunk in bytes (5MB)
 */

const CHUNK_SIZE = 5 * 1024 * 1024; // 5MB - S3 minimum part size (changed from 2.5MB)
const MAX_FILE_SIZE = 200 * 1024 * 1024; // 10MB
const MAX_PARTS = 10000; // S3 maximum parts allowed

/**
 * Calculate optimal chunking strategy based on file size
 * @param fileSize Total file size in bytes
 * @returns { chunkSize, numChunks } Optimal chunk size and number of chunks
 */
const calculateChunkingStrategy = (fileSize: number) => {
	// For small files (< 10MB), use a single chunk
	if (fileSize < 10 * 1024 * 1024) {
		return {
			chunkSize: fileSize,
			numChunks: 1,
		};
	}

	// For files where we'll need multiple chunks, calculate optimal strategy

	// 1. Start with minimum part size (5MB)
	let chunkSize = CHUNK_SIZE;

	// 2. Calculate how many chunks we'd have with minimum size
	let numChunks = Math.ceil(fileSize / chunkSize);

	// 3. If we'd have too many chunks (unlikely but possible for huge files),
	//    increase chunk size to stay under MAX_PARTS limit
	if (numChunks > MAX_PARTS) {
		chunkSize = Math.ceil(fileSize / MAX_PARTS);
		numChunks = MAX_PARTS;
	}

	// 4. Special optimizations for different file size ranges

	// For 10-25MB files, use 4 chunks for better performance
	if (fileSize > 10 * 1024 * 1024 && fileSize <= 25 * 1024 * 1024) {
		numChunks = 4;
		chunkSize = Math.ceil(fileSize / numChunks);
	}
	// For 25-100MB files, use 8-16 chunks for balanced performance
	else if (fileSize > 25 * 1024 * 1024 && fileSize <= 100 * 1024 * 1024) {
		// Target ~10 chunks for medium files
		const targetChunks = Math.min(16, Math.max(8, Math.ceil(fileSize / (10 * 1024 * 1024))));
		numChunks = targetChunks;
		chunkSize = Math.ceil(fileSize / numChunks);
	}
	// For large files (>100MB), use larger chunks for efficiency
	else if (fileSize > 100 * 1024 * 1024) {
		// Target larger chunks (15-20MB) for large files for better throughput
		chunkSize = Math.max(CHUNK_SIZE, Math.min(20 * 1024 * 1024, Math.ceil(fileSize / 50)));
		numChunks = Math.ceil(fileSize / chunkSize);
	}

	// 5. Final safety check - ensure chunk size is at least minimum
	if (chunkSize < CHUNK_SIZE) {
		chunkSize = CHUNK_SIZE;
		numChunks = Math.ceil(fileSize / chunkSize);
	}

	return { chunkSize, numChunks };
};

export interface MultipartUploadOptions {
	allowedFormats?: string[];
	maxFileSize?: number;
	onProgress?: (progress: number, fileName: string) => void;
	onError?: (error: string, fileName: string) => void;
	onComplete?: (results: any[]) => void;
	onFileStart?: (fileName: string) => void;
	onFileComplete?: (fileName: string, result: any) => void;
}

export const useMultipartUpload = ({
	allowedFormats = [
		'.pdf',
		'.doc',
		'.docx',
		'.mp4',
		'.jpg',
		'.jpeg',
		'.png',
		'.gif',
		'.heic',
		'.webp',
		'.heif',
		'.xls',
		'.xlsx',
		'.ppt',
		'.kmz',
		'.kml',
		'.txt',
		'.csv',
		'.json',
		'.wav',
	],
	maxFileSize = MAX_FILE_SIZE,
	onProgress,
	onError,
	onComplete,
	onFileStart,
	onFileComplete,
}: MultipartUploadOptions = {}) => {
	// Use selectors to get reactive state
	const uploadStatus = useUploadStore((state) => state.uploadStatus);
	const setUploadStatus = useUploadStore((state) => state.setUploadStatus);
	const resetStatus = useUploadStore((state) => state.resetStatus);
	const removeUploadStatusByFileName = useUploadStore((state) => state.removeUploadStatusByFileName);
	const globalLoading = useUploadStore((state) => state.globalLoading);
	const setGlobalLoading = useUploadStore((state) => state.setGlobalLoading);
	const incrementRetry = useUploadStore((state) => state.incrementRetry);
	const togglePause = useUploadStore((state) => state.togglePause);
	const updateParts = useUploadStore((state) => state.updateParts);
	const maxRetries = useUploadStore((state) => state.maxRetries);
	const maxConcurrentUploads = useUploadStore((state) => state.maxConcurrentUploads);
	const showSnackbar = useSnackbarStore((state) => state.showSnackbar);
	const { t } = useTranslation();

	const validateFile = (file: File): { isValid: boolean; error: string | null } => {
		if (file.size > maxFileSize) {
			const maxSizeMB = Math.floor(maxFileSize / (1024 * 1024));
			showSnackbar(t('error'), t('fileSizeExceedsLimit', { maxFileSize: maxSizeMB }), 'error');
			return { isValid: false, error: 'fileSizeErrorMessage' };
		}

		if (allowedFormats.length > 0) {
			const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
			if (!allowedFormats.includes(fileExtension)) {
				showSnackbar(t('error'), t('fileFormatNotSupported'), 'error');
				return { isValid: false, error: 'fileSupportErrorMessage' };
			}
		}

		return { isValid: true, error: null };
	};

	const uploadChunk = async (url: string, chunk: Blob, retryCount = 0): Promise<string> => {
		try {
			const response = await fetch(url, {
				method: 'PUT',
				headers: {
					'Content-Type': 'application/octet-stream',
					'Content-Length': chunk.size.toString(),
				},
				body: chunk,
			});

			if (!response.ok) {
				throw new Error(`Chunk upload failed: ${response.statusText}`);
			}

			const eTag = response.headers.get('ETag');
			if (!eTag) {
				throw new Error('Missing ETag in response headers');
			}

			return eTag.replace(/"/g, '');
		} catch (error: any) {
			if (retryCount < maxRetries) {
				await new Promise((resolve) => setTimeout(resolve, 1000 * (retryCount + 1)));
				return uploadChunk(url, chunk, retryCount + 1);
			}
			throw error;
		}
	};

	const uploadFile = async (file: File, fileIndex: number): Promise<any> => {
		const { isValid, error } = validateFile(file);
		if (!isValid) {
			setUploadStatus(fileIndex, {
				fileName: file.name,
				progress: 0,
				isCompleted: false,
				error,
				key: '', // Clear key for validation errors
				uploadId: '', // Clear uploadId for validation errors 
				retryCount: 0,
				isPaused: false,
			});
			if (onError) {
				try {
					onError(error || 'Unknown error', file.name);
				} catch (err) {
					console.log('Error in onError callback');
				}
			}
			return null;
		}

		// Initialize upload session - failures here should not be retried
		let key: string, uploadId: string;
		const existingStatus = uploadStatus[fileIndex];

		if (existingStatus?.key && existingStatus?.uploadId) {
			// Reuse existing upload session
			key = existingStatus.key;
			uploadId = existingStatus.uploadId;
		} else {
			// Only initiate new upload if we don't have an existing session
			try {
				const {
					content: { key: newKey, uploadId: newUploadId },
				} = await initiateUpload(file.name);
				key = newKey;
				uploadId = newUploadId;
			} catch (initiateError: any) {
				// Initialization failed - show error immediately, NO retries
				const errorMessage = `Failed to initialize upload: ${initiateError.message || 'Unknown error'}`;
				
				setUploadStatus(fileIndex, {
					fileName: file.name,
					progress: 0,
					isCompleted: false,
					error: errorMessage,
					key: '',
					uploadId: '',
					retryCount: 0,
					isPaused: false,
				});
				
				// Show user-friendly error notification
				showSnackbar(
					t('uploadFailed') || 'Upload Failed',
					t('failedToInitializeUpload', { fileName: file.name }) || `Failed to initialize upload for "${file.name}".`,
					'error'
				);
				
				if (onError) {
					try {
						onError(errorMessage, file.name);
					} catch (err) {
						console.log('Error in onError callback');
					}
				}
				
				// Return null - this will NOT trigger any retry logic
				return null;
			}
		}

		try {
			if (onFileStart) {
				try {
					onFileStart(file.name);
				} catch (err) {
					console.log('Error in onFileStart callback');
				}
			}

			setUploadStatus(fileIndex, {
				fileName: file.name,
				progress: 0,
				isCompleted: false,
				error: null,
				key,
				retryCount: existingStatus?.retryCount || 0,
				isPaused: false,
				uploadId,
			});

			const { chunkSize, numChunks } = calculateChunkingStrategy(file.size);

			const parts: { partNumber: number; eTag: string }[] = [];

			for (let i = 0; i < numChunks; i++) {
				const currentStatus = uploadStatus[fileIndex];
				if (currentStatus?.isPaused) {
					await new Promise((resolve) => {
						const checkPause = () => {
							if (!uploadStatus[fileIndex]?.isPaused) {
								resolve(true);
							} else {
								setTimeout(checkPause, 100);
							}
						};
						checkPause();
					});
				}

				const start = i * chunkSize;
				const end = i === numChunks - 1 ? file.size : Math.min((i + 1) * chunkSize, file.size);
				const chunk = file.slice(start, end);
				const { url } = await getPartUrl(file.name, key, uploadId, i + 1);
				const eTag = await uploadChunk(url, chunk);

				parts.push({ partNumber: i + 1, eTag });
				updateParts(fileIndex, parts);

				const progress = Math.round(((i + 1) / numChunks) * 100);
				setUploadStatus(fileIndex, {
					fileName: file.name,
					progress,
					isCompleted: false,
					key,
					error: null,
					retryCount: 0,
					isPaused: false,
					uploadId,
					parts,
				});

				if (onProgress) {
					try {
						onProgress(progress, file.name);
					} catch (err) {
						console.log('Error in onProgress callback');
					}
				}
			}

			if (parts.length === 0) {
				throw new Error('No parts were uploaded successfully');
			}

			const sortedParts = parts.sort((a, b) => a.partNumber - b.partNumber);
			const expectedPartNumbers = Array.from({ length: numChunks }, (_, i) => i + 1);
			const actualPartNumbers = sortedParts.map(p => p.partNumber);
			const missingParts = expectedPartNumbers.filter(pn => !actualPartNumbers.includes(pn));
			
			if (missingParts.length > 0) {
				throw new Error(`Missing parts: ${missingParts.join(', ')}`);
			}

			await new Promise(resolve => setTimeout(resolve, 100));
			const getCurrentUploadStatus = () => useUploadStore.getState().uploadStatus[fileIndex];
			const currentUploadStatus = getCurrentUploadStatus();
			
			const finalUploadId = currentUploadStatus?.uploadId || uploadId;
			const finalKey = currentUploadStatus?.key || key;
			
			// Verify we still have a valid upload session
			if (!finalUploadId || !finalKey) {
				console.warn(`Upload session lost for ${file.name}:`, { 
					currentUploadStatus, 
					originalUploadId: uploadId, 
					originalKey: key 
				});
				throw new Error(`Upload session lost - cannot complete upload for "${file.name}"`);
			}
	

			try {
				await completeUpload(finalKey, finalUploadId, sortedParts);
			} catch (completeError: any) {
				console.error(`Complete upload failed for ${file.name}:`, {
					error: completeError,
					key: finalKey,
					uploadId: finalUploadId,
					partsCount: sortedParts.length,
					errorMessage: completeError.message,
					errorResponse: completeError.response?.data
				});

				// Check if it's a NoSuchUpload error - this might indicate a session timeout or invalid state
				if (completeError.message?.includes('NoSuchUpload') || completeError.response?.status === 404) {
					const errorMessage = `Upload session expired or invalid for "${file.name}". Please try uploading the file again.`;
					
					setUploadStatus(fileIndex, {
						fileName: file.name,
						progress: 0,
						isCompleted: false,
						error: errorMessage,
						key: '', 
						uploadId: '', 
						retryCount: 0,
						isPaused: false,
						parts: [], 
					});
					
					showSnackbar(
						t('uploadSessionExpired') || 'Upload Session Expired',
						t('uploadSessionExpiredMessage', { fileName: file.name }) || errorMessage,
						'error'
					);
					
					if (onError) {
						try {
							onError(errorMessage, file.name);
						} catch (err) {
							console.log('Error in onError callback');
						}
					}
					
					return null;
				}

				const errorMessage = `Failed to complete upload: ${completeError.message || 'Unknown error'}`;
				
				setUploadStatus(fileIndex, {
					fileName: file.name,
					progress: 100,
					isCompleted: false,
					error: errorMessage,
					key: finalKey,
					uploadId: finalUploadId,
					retryCount: 0,
					isPaused: false,
					parts: sortedParts,
				});
				
				showSnackbar(
					t('uploadFailed') || 'Upload Failed',
					t('failedToCompleteUpload', { fileName: file.name }) || `Failed to complete upload for "${file.name}".`,
					'error'
				);
				
				if (onError) {
					try {
						onError(errorMessage, file.name);
					} catch (err) {
						console.log('Error in onError callback');
					}
				}
				
				return null;
			}

			const finalStatus = {
				fileName: file.name,
				progress: 100,
				isCompleted: true,
				key,
				error: null,
				retryCount: 0,
				isPaused: false,
				uploadId,
				parts,
			};

			setUploadStatus(fileIndex, finalStatus);
			if (onFileComplete) {
				try {
					onFileComplete(file.name, finalStatus);
				} catch (err) {
					console.log('Error in onFileComplete callback');
				}
			}
			return finalStatus;
		} catch (error: any) {
			const currentRetryCount = uploadStatus[fileIndex]?.retryCount || 0;
			
			// Only retry upload process errors (not initialization errors)
			if (currentRetryCount < maxRetries) {
				incrementRetry(fileIndex);
				
				// Add a small delay before retrying to prevent rapid successive calls
				await new Promise(resolve => setTimeout(resolve, 1000 * (currentRetryCount + 1)));
				
				return uploadFile(file, fileIndex);
			}

			// Max retries exceeded - mark as failed
			const existingStatus = uploadStatus[fileIndex];
			setUploadStatus(fileIndex, {
				fileName: file.name,
				progress: 0,
				isCompleted: false,
				key: existingStatus?.key || '', // Preserve existing key
				uploadId: existingStatus?.uploadId || '', // Preserve existing uploadId
				error: error.message || `Upload failed after ${maxRetries} attempts`,
				retryCount: currentRetryCount,
				isPaused: false,
			});

			// Show error notification for failed upload
			showSnackbar(
				t('uploadFailed') || 'Upload Failed',
				t('fileUploadFailedAfterRetries', { fileName: file.name, maxRetries }) || 
				`Failed to upload "${file.name}" after ${maxRetries} attempts.`,
				'error'
			);

			onError?.(error.message || 'Upload failed', file.name);
			return null;
		}
	};

	const uploadFiles = async (files: File[]) => {
		if (files.length === 0) {
			if (onError) {
				try {
					onError('No files provided', '');
				} catch (err) {
					console.log('Error in onError callback');
				}
			}
			return;
		}

		setGlobalLoading(true);
		resetStatus();

		try {
			const uploadPromises: Promise<any>[] = [];
			const results = [];

			for (let i = 0; i < files.length; i++) {
				const file = files[i];
				const { isValid, error } = validateFile(file);

				if (!isValid) {
					setUploadStatus(i, {
						fileName: file.name,
						progress: 0,
						isCompleted: false,
						error,
						key: '',
						retryCount: 0,
						isPaused: false,
					});
					if (onError) {
						try {
							onError(error || 'Unknown error', file.name);
						} catch (err) {
							console.log('Error in onError callback');
						}
					}
					continue;
				}

				const uploadPromise = uploadFile(file, i);
				uploadPromises.push(uploadPromise);

				if (uploadPromises.length >= maxConcurrentUploads) {
					const result = await Promise.race(uploadPromises);
					if (result) results.push(result);
					uploadPromises.splice(uploadPromises.indexOf(uploadPromise), 1);
				}
			}

			const remainingResults = await Promise.all(uploadPromises);
			results.push(...remainingResults.filter(Boolean));

			if (onComplete) {
				try {
					onComplete(results);
				} catch (err) {
					console.log('Error in onComplete callback');
				}
			}
			return results;
		} finally {
			setGlobalLoading(false);
		}
	};

	const isAllCompleted = uploadStatus.every((status: any) => status?.isCompleted);
	const failedUploads = uploadStatus.filter((status: any) => status && status.error);
	const inProgressUploads = uploadStatus.filter(
		(status: any) => status && !status.isCompleted && !status.error && !status.isPaused
	);

	return {
		uploadStatus,
		uploadFiles,
		resetStatus,
		isAllCompleted,
		globalLoading,
		isLoading: globalLoading,
		failedUploads,
		inProgressUploads,
		togglePause,
		removeUploadStatusByFileName,
	};
};
